import { useState, type FC, useEffect, type Dispatch, type SetStateAction } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type PreparePaymentRequest, preparePayment, type NabTransactOptions, type Gateway } from '@/api/registration/payment';
import { type ApiResponse } from '@/api/tenantClient';
import { useCartQuery } from '@/registration/hook/useCartQuery';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { SkeletonText } from '@chakra-ui/react';
import PayByNabTransact from './PayByNabTransact';
import { cartQueryKey } from '@/api/registration';

export interface PayByNabTransactWrapperProps {
  setErrors: Dispatch<SetStateAction<string[]>>
  isValid: boolean
}

const PayByNabTransactWrapper: FC<PayByNabTransactWrapperProps> = ({
  setErrors,
  isValid,
  ...props
}) => {
  const [options, setOptions] = useState<NabTransactOptions>();
  const { createTenantApiRequest } = useTenantApi();
  const { eventId } = useRegisterRoute();
  const queryClient = useQueryClient();

  const { data: cart } = useCartQuery(eventId);

  const { mutate: preparePaymentMutate, isLoading } = useMutation<ApiResponse<NabTransactOptions>, Error, PreparePaymentRequest>({
    mutationFn: async (preparePaymentData: PreparePaymentRequest) => {
      return await preparePayment(createTenantApiRequest)(preparePaymentData) as ApiResponse<NabTransactOptions>;
    },
    onSuccess: (result) => {
      if (result.errors && result.errors.length > 0) {
        setErrors(result.errors);
      } else {
        void queryClient.invalidateQueries({ queryKey: [cartQueryKey, { registrationId: cart?.registrationId }] });
        setOptions(result.items[0]);
      }
    },
    onError: (error: any) => {
      setErrors([error.message ?? 'Failed to prepare NAB Transact payment.']);
    }
  });

  useEffect(() => {
    if (cart) {
      const preparePaymentData: PreparePaymentRequest = {
        cartId: cart.id,
        locale: 'aud',
        gateway: 'nab_transact' as Gateway
      };
      setErrors([]);
      preparePaymentMutate(preparePaymentData);
    }
  }, [cart]);

  return isLoading ? (
    <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='8' />
  ) : options?.order && (
    <PayByNabTransact
      setErrors={setErrors}
      isValid={isValid}
      options={options}
    />
  );
};

export default PayByNabTransactWrapper;
